/**
 * Fair Brands pages — catalog + detail (demo until brand API is wired).
 */

import {
  FAIR_HOME_BRANDS_DEMO,
  type FairHomeBrandCard,
} from '@/widgets/home/fair/fair-home-brands.data'
import {
  getFairHomeProductsCardDevice,
  getFairHomeProductsColumns,
  getFairHomeProductsGap,
  resolveFairHomeProducts,
  showFairHomeProductsDensityToggle,
  type FairHomeProductCard,
  type FairHomeProductsDensity,
} from '@/widgets/home/fair/fair-home-products.data'

export const FAIR_BRANDS_PAGE_DEFAULT_AUDIENCE = 'women'
export const FAIR_BRANDS_PAGE_SIZE = 20
export const FAIR_BRANDS_PAGE_AI_TIP_KEY = 'based_on_most_viewed' as const

export type FairBrandsSort = 'asc' | 'desc'

export interface FairBrandCatalogItem {
  id: number
  name: string
  letter: string
  categories: string[]
  /** Present for popular / detail hero media */
  backgroundSrc?: string
  logoSrc?: string
  logoVariant?: FairHomeBrandCard['logoVariant']
  isDemo?: boolean
}

export interface FairBrandDetailResolved {
  brand: FairBrandCatalogItem
  description: string
  products: FairHomeProductCard[]
  similar: FairHomeBrandCard[]
  isFallback: boolean
}

export interface FairBrandLetterGroup {
  letter: string
  brands: FairBrandCatalogItem[]
}

export type FairBrandAlphabetId = 'latin' | 'cyrillic' | 'digits'

export interface FairBrandAlphabetSection {
  id: FairBrandAlphabetId
  titleKey: 'latin' | 'cyrillic' | 'digits'
  /** Full index strip (A–Z / А–Я / 0–9) */
  indexLetters: string[]
  /** Only letters that have brands after filters */
  groups: FairBrandLetterGroup[]
}

export const FAIR_BRANDS_LATIN_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')
export const FAIR_BRANDS_CYRILLIC_LETTERS =
  'АБВГДЕЁЖЗИЙКЛМНОПРСТУФХЦЧШЩЪЫЬЭЮЯ'.split('')
export const FAIR_BRANDS_DIGIT_LETTERS = '0123456789'.split('')

export const FAIR_BRANDS_ALPHABET_DEFS: Array<{
  id: FairBrandAlphabetId
  titleKey: FairBrandAlphabetId
  letters: string[]
}> = [
  {
    id: 'latin',
    titleKey: 'latin',
    letters: FAIR_BRANDS_LATIN_LETTERS,
  },
  {
    id: 'cyrillic',
    titleKey: 'cyrillic',
    letters: FAIR_BRANDS_CYRILLIC_LETTERS,
  },
  {
    id: 'digits',
    titleKey: 'digits',
    letters: FAIR_BRANDS_DIGIT_LETTERS,
  },
]

const LATIN_SET = new Set(FAIR_BRANDS_LATIN_LETTERS)
const CYRILLIC_SET = new Set(FAIR_BRANDS_CYRILLIC_LETTERS)
const DIGIT_SET = new Set(FAIR_BRANDS_DIGIT_LETTERS)

export function normalizeFairBrandLetter(letter: string | null | undefined): string {
  if (!letter) return ''
  const value = String(letter).trim()
  if (!value) return ''
  if (/\d/.test(value.charAt(0))) return value.charAt(0)
  return value.charAt(0).toLocaleUpperCase('ru-RU')
}

export function getFairBrandAlphabetId(
  letter: string,
): FairBrandAlphabetId | null {
  const normalized = normalizeFairBrandLetter(letter)
  if (!normalized) return null
  if (DIGIT_SET.has(normalized)) return 'digits'
  if (LATIN_SET.has(normalized)) return 'latin'
  if (CYRILLIC_SET.has(normalized)) return 'cyrillic'
  return null
}

export const FAIR_BRANDS_DEMO_CATEGORIES = [
  'Одежда',
  'Обувь',
  'Сумки',
  'Аксессуары',
  'Косметика',
  'Спорт',
  'Люкс',
  'Детям',
] as const

const EXTRA_CATALOG_NAMES: Array<{ name: string; categories: string[] }> = [
  { name: 'Adidas', categories: ['Одежда', 'Обувь', 'Спорт'] },
  { name: 'A Bathing Ape', categories: ['Одежда', 'Обувь'] },
  { name: 'Burberry', categories: ['Одежда', 'Люкс', 'Сумки'] },
  { name: 'Calvin Klein', categories: ['Одежда', 'Аксессуары'] },
  { name: 'Cos', categories: ['Одежда'] },
  { name: 'Diesel', categories: ['Одежда', 'Аксессуары'] },
  { name: 'Gucci', categories: ['Одежда', 'Люкс', 'Сумки'] },
  { name: 'H&M', categories: ['Одежда', 'Детям'] },
  { name: "Levi's", categories: ['Одежда'] },
  { name: 'Massimo Dutti', categories: ['Одежда'] },
  { name: 'New Balance', categories: ['Обувь', 'Спорт'] },
  { name: 'Off-White', categories: ['Одежда', 'Обувь'] },
  { name: 'Puma', categories: ['Одежда', 'Обувь', 'Спорт'] },
  { name: 'Ralph Lauren', categories: ['Одежда', 'Аксессуары'] },
  { name: 'Stone Island', categories: ['Одежда'] },
  { name: 'The North Face', categories: ['Одежда', 'Спорт'] },
  { name: 'Tommy Hilfiger', categories: ['Одежда'] },
  { name: 'Uniqlo', categories: ['Одежда', 'Детям'] },
  { name: 'Zara', categories: ['Одежда', 'Аксессуары'] },
  { name: '0-100', categories: ['Аксессуары'] },
  { name: '1017 ALYX 9SM', categories: ['Одежда', 'Аксессуары'] },
  { name: 'Адидас', categories: ['Одежда', 'Спорт'] },
  { name: 'Зара', categories: ['Одежда'] },
]

const POPULAR_CATEGORY_BY_NAME: Record<string, string[]> = {
  Prada: ['Одежда', 'Люкс', 'Сумки'],
  'Louis Vuitton': ['Одежда', 'Люкс', 'Сумки'],
  Dior: ['Одежда', 'Люкс', 'Косметика'],
  Balenciaga: ['Одежда', 'Обувь', 'Люкс'],
  Nike: ['Одежда', 'Обувь', 'Спорт'],
  Chanel: ['Одежда', 'Люкс', 'Косметика'],
  'Tory Burch': ['Одежда', 'Сумки'],
  Hermès: ['Одежда', 'Люкс', 'Сумки'],
}

const DETAIL_COPY: Record<string, string> = {
  Prada:
    'Итальянский дом с чётким силуэтом и сильным акцентом на сумки и обувь.',
  'Louis Vuitton':
    'Люксовый дом с узнаваемыми монограммами, travel-наследием и ready-to-wear.',
  Dior: 'Французский couture и современный ready-to-wear с сильным beauty-направлением.',
  Balenciaga:
    'Авангардный люкс: объёмы, street-влияние и культовая обувь.',
  Nike: 'Спортивный бренд с кроссовками, экипировкой и коллаборациями.',
  Chanel:
    'Классика haute couture, tweed, сумки и iconic beauty-линейка.',
  default:
    'Подборка товаров бренда — демо-данные, пока API бренда не подключён.',
}

function getBrandLetter(name: string): string {
  const ch = name.trim().charAt(0)
  if (!ch) return '#'
  if (/\d/.test(ch)) return ch
  return ch.toLocaleUpperCase('ru-RU')
}

function uniqueByName(items: FairBrandCatalogItem[]): FairBrandCatalogItem[] {
  const seen = new Set<string>()
  const result: FairBrandCatalogItem[] = []
  for (const item of items) {
    const key = item.name.toLocaleLowerCase('ru-RU')
    if (seen.has(key)) continue
    seen.add(key)
    result.push(item)
  }
  return result
}

function buildPopularCatalogItems(): FairBrandCatalogItem[] {
  const uniquePopular = uniqueByName(
    FAIR_HOME_BRANDS_DEMO.map((card) => ({
      id: card.id,
      name: card.name,
      letter: getBrandLetter(card.name),
      categories: POPULAR_CATEGORY_BY_NAME[card.name] ?? ['Одежда'],
      backgroundSrc: card.backgroundSrc,
      logoSrc: card.logoSrc,
      logoVariant: card.logoVariant,
      isDemo: true,
    })),
  )
  return uniquePopular
}

function buildFullCatalog(): FairBrandCatalogItem[] {
  const popular = buildPopularCatalogItems()
  const extras: FairBrandCatalogItem[] = EXTRA_CATALOG_NAMES.map(
    (item, index) => ({
      id: -(100 + index),
      name: item.name,
      letter: getBrandLetter(item.name),
      categories: item.categories,
      isDemo: true,
    }),
  )
  return uniqueByName([...popular, ...extras])
}

export const FAIR_BRANDS_DEMO_CATALOG = buildFullCatalog()

export const FAIR_BRANDS_DEMO_POPULAR: FairHomeBrandCard[] = (() => {
  const seen = new Set<string>()
  const result: FairHomeBrandCard[] = []
  for (const card of FAIR_HOME_BRANDS_DEMO) {
    const key = card.name.toLocaleLowerCase('ru-RU')
    if (seen.has(key)) continue
    seen.add(key)
    result.push(card)
    if (result.length >= 8) break
  }
  return result
})()

function buildMockProducts(count: number): FairHomeProductCard[] {
  const base = resolveFairHomeProducts(null, { allowDemoFallback: true })
  if (base.length === 0) {
    return []
  }
  return Array.from({ length: count }, (_, index) => {
    const source = base[index % base.length]!
    const id = -(index + 1)
    return {
      ...source,
      id,
      variantId: id,
      isDemo: true,
    }
  })
}

const FAIR_BRANDS_MOCK_PRODUCTS = buildMockProducts(FAIR_BRANDS_PAGE_SIZE * 2)

export function brandLetterSectionId(letter: string): string {
  return `fair-brands-letter-${encodeURIComponent(normalizeFairBrandLetter(letter))}`
}

export function brandAlphabetSectionId(id: FairBrandAlphabetId): string {
  return `fair-brands-alphabet-${id}`
}

export function filterFairBrandsCatalog(
  brands: FairBrandCatalogItem[],
  options: {
    search?: string
    categories?: string[]
    sort?: FairBrandsSort
  },
): FairBrandAlphabetSection[] {
  const search = (options.search ?? '').trim().toLocaleLowerCase('ru-RU')
  const categories = options.categories ?? []
  const sort = options.sort ?? 'asc'

  let filtered = brands
  if (search) {
    filtered = filtered.filter((brand) =>
      brand.name.toLocaleLowerCase('ru-RU').includes(search),
    )
  }
  if (categories.length > 0) {
    filtered = filtered.filter((brand) =>
      categories.some((category) => brand.categories.includes(category)),
    )
  }

  const groups = new Map<string, FairBrandCatalogItem[]>()
  for (const brand of filtered) {
    const list = groups.get(brand.letter) ?? []
    list.push(brand)
    groups.set(brand.letter, list)
  }

  const sections: FairBrandAlphabetSection[] = []

  for (const def of FAIR_BRANDS_ALPHABET_DEFS) {
    const letterOrder =
      sort === 'desc' ? [...def.letters].reverse() : def.letters

    const letterGroups: FairBrandLetterGroup[] = []
    for (const letter of letterOrder) {
      const list = groups.get(letter)
      if (!list?.length) continue
      const sorted = [...list].sort((a, b) =>
        a.name.localeCompare(b.name, 'ru', { sensitivity: 'base' }),
      )
      if (sort === 'desc') {
        sorted.reverse()
      }
      letterGroups.push({ letter, brands: sorted })
    }

    if (letterGroups.length === 0) continue

    sections.push({
      id: def.id,
      titleKey: def.titleKey,
      indexLetters: letterOrder,
      groups: letterGroups,
    })
  }

  return sections
}

export function findFairBrandByName(
  brandName: string | null | undefined,
): FairBrandCatalogItem | undefined {
  if (!brandName) return undefined
  const key = decodeURIComponent(String(brandName))
    .trim()
    .toLocaleLowerCase('ru-RU')
  return FAIR_BRANDS_DEMO_CATALOG.find(
    (brand) => brand.name.toLocaleLowerCase('ru-RU') === key,
  )
}

export function resolveFairBrandDetail(
  brandName: string | null | undefined,
): FairBrandDetailResolved {
  const found = findFairBrandByName(brandName)
  const brand = found ?? FAIR_BRANDS_DEMO_CATALOG[0]!
  const isFallback = !found

  const media =
    brand.backgroundSrc && brand.logoSrc
      ? brand
      : FAIR_BRANDS_DEMO_POPULAR.find(
          (item) =>
            item.name.toLocaleLowerCase('ru-RU')
            === brand.name.toLocaleLowerCase('ru-RU'),
        ) ?? FAIR_BRANDS_DEMO_POPULAR[0]!

  const enriched: FairBrandCatalogItem = {
    ...brand,
    backgroundSrc: brand.backgroundSrc ?? media.backgroundSrc,
    logoSrc: brand.logoSrc ?? media.logoSrc,
    logoVariant: brand.logoVariant ?? media.logoVariant ?? 'tint',
  }

  const similar = FAIR_BRANDS_DEMO_POPULAR.filter(
    (item) =>
      item.name.toLocaleLowerCase('ru-RU')
      !== enriched.name.toLocaleLowerCase('ru-RU'),
  ).slice(0, 6)

  return {
    brand: enriched,
    description: DETAIL_COPY[enriched.name] ?? DETAIL_COPY.default!,
    products: FAIR_BRANDS_MOCK_PRODUCTS.map((product) => ({
      ...product,
      brand: enriched.name,
    })),
    similar,
    isFallback,
  }
}

export function sliceFairBrandProducts(
  products: FairHomeProductCard[],
  limit: number,
): FairHomeProductCard[] {
  return products.slice(0, Math.max(0, limit))
}

export function canFairBrandLoadMore(total: number, limit: number): boolean {
  return limit < total
}

export {
  getFairHomeProductsCardDevice,
  getFairHomeProductsColumns,
  getFairHomeProductsGap,
  showFairHomeProductsDensityToggle,
}

export type { FairHomeBrandCard, FairHomeProductCard, FairHomeProductsDensity }
